<template>
  <div class="mb-4">
    <div class="flex justify-between items-center mb-1">
      <label class="block text-sm font-semibold">
        {{ $t("message.image_generator.prompt_templates") }}
      </label>
      <div class="flex gap-3">
        <button
          v-if="appliedTemplate"
          class="text-xs text-red-600 hover:underline disabled:opacity-40 disabled:no-underline"
          :disabled="!hasChanges || updating"
          :title="appliedTemplate.name"
          @click="update()"
        >
          <i class="mdi mdi-content-save-edit"></i>
          {{
            updating
              ? $t("message.image_generator.saving")
              : $t("message.image_generator.update_template")
          }}
        </button>
        <button
          class="text-xs text-red-600 hover:underline"
          :disabled="!canSave"
          @click="showForm = !showForm"
        >
          <i class="mdi mdi-content-save"></i>
          {{ $t("message.image_generator.save_as_template") }}
        </button>
      </div>
    </div>

    <div v-if="templates.length" class="flex flex-wrap gap-2 mb-2">
      <div
        v-for="template in templates"
        :key="template.id"
        class="flex items-center border rounded text-xs overflow-hidden"
        :class="
          appliedId === template.id
            ? 'border-red-600 bg-red-50'
            : 'border-gray-300'
        "
      >
        <button
          class="px-2 py-1 hover:bg-gray-100"
          :title="template.description || template.prompt"
          @click="apply(template)"
        >
          {{ template.name }}
        </button>
        <button
          class="px-1 py-1 text-gray-400 hover:text-red-600 border-l"
          :title="$t('message.image_generator.delete_template')"
          @click="remove(template)"
        >
          <i class="mdi mdi-close text-xs"></i>
        </button>
      </div>
    </div>
    <p v-else-if="!loading" class="text-xs text-gray-400 mb-2">
      {{ $t("message.image_generator.no_templates") }}
    </p>

    <div v-if="showForm" class="border rounded p-3 bg-gray-50">
      <input
        v-model="name"
        type="text"
        class="regular clear full mb-2"
        :placeholder="$t('message.image_generator.template_name')"
      />
      <p class="text-xs text-gray-500 mb-2">
        {{ $t("message.image_generator.save_template_hint") }}
      </p>
      <button
        class="w-full bg-gray-800 text-white text-sm py-1 rounded disabled:opacity-50"
        :disabled="saving || !name.trim()"
        @click="save()"
      >
        {{
          saving
            ? $t("message.image_generator.saving")
            : $t("message.image_generator.save_template")
        }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  getPromptTemplates,
  createPromptTemplate,
  updatePromptTemplate,
  deletePromptTemplate
} from "@/api/store/AiService.js";

export default {
  name: "AiPromptTemplates",
  props: {
    // Estado actual del formulario, para guardarlo como plantilla.
    current: {
      type: Object,
      required: true
    },
    appliedId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      templates: [],
      loading: false,
      showForm: false,
      saving: false,
      updating: false,
      name: ""
    };
  },
  computed: {
    canSave() {
      return !!(this.current.prompt || "").trim();
    },
    appliedTemplate() {
      return this.templates.find(t => t.id === this.appliedId) || null;
    },
    // Sólo tiene sentido actualizar si el formulario difiere de lo guardado.
    hasChanges() {
      const template = this.appliedTemplate;
      if (!template) return false;
      const sameAssets =
        JSON.stringify([...(this.current.assetIds || [])].sort()) ===
        JSON.stringify([...this.parsedIds(template)].sort());
      return (
        (this.current.prompt || "").trim() !== (template.prompt || "").trim() ||
        (this.current.aspectRatio || null) !== (template.aspectRatio || null) ||
        (this.current.imageSize || null) !== (template.imageSize || null) ||
        Number(this.current.count || 0) !== Number(template.imagesCount || 0) ||
        !sameAssets
      );
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    // CI devuelve los enteros de MySQL como strings.
    normalize(template) {
      return { ...template, id: Number(template.id) };
    },
    async load() {
      this.loading = true;
      try {
        const data = await getPromptTemplates();
        this.templates = (data.templates || []).map(this.normalize);
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.templates_error"));
      } finally {
        this.loading = false;
      }
    },
    // asset_ids se guarda como JSON en una columna de texto.
    parsedIds(template) {
      try {
        return template.assetIds ? JSON.parse(template.assetIds) : [];
      } catch (e) {
        return [];
      }
    },
    /**
     * Pisa la plantilla aplicada con lo que hay ahora en el formulario, en vez
     * de crear una nueva: es el caso de "el prompt de siempre, ajustado".
     */
    async update() {
      const template = this.appliedTemplate;
      if (!template || !this.hasChanges) return;
      if (
        !confirm(
          this.$t("message.image_generator.confirm_update_template", {
            name: template.name
          })
        )
      ) {
        return;
      }
      this.updating = true;
      try {
        const changes = {
          prompt: this.current.prompt,
          aspectRatio: this.current.aspectRatio,
          imageSize: this.current.imageSize,
          imagesCount: this.current.count,
          assetIds: this.current.assetIds
        };
        const data = await updatePromptTemplate(template.id, changes);
        // Se refleja en la lista local para que el botón vuelva a deshabilitarse.
        const index = this.templates.findIndex(t => t.id === template.id);
        this.$set(this.templates, index, this.normalize(data.template));
        this.$emit("changed");
        this.$toast.success(
          this.$t("message.image_generator.template_updated")
        );
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.error ||
            this.$t("message.image_generator.templates_error")
        );
      } finally {
        this.updating = false;
      }
    },
    apply(template) {
      const assetIds = this.parsedIds(template);
      this.$emit("apply", {
        id: template.id,
        prompt: template.prompt,
        aspectRatio: template.aspectRatio,
        imageSize: template.imageSize,
        count: template.imagesCount ? Number(template.imagesCount) : null,
        assetIds
      });
    },
    async save() {
      this.saving = true;
      try {
        const data = await createPromptTemplate({
          name: this.name.trim(),
          prompt: this.current.prompt,
          aspectRatio: this.current.aspectRatio,
          imageSize: this.current.imageSize,
          imagesCount: this.current.count,
          assetIds: this.current.assetIds
        });
        const created = this.normalize(data.template);
        this.templates.push(created);
        // Queda aplicada: así los siguientes ajustes pueden actualizarla en vez
        // de crear otra plantilla igual.
        this.apply(created);
        this.name = "";
        this.showForm = false;
        this.$toast.success(this.$t("message.image_generator.template_saved"));
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.error ||
            this.$t("message.image_generator.templates_error")
        );
      } finally {
        this.saving = false;
      }
    },
    async remove(template) {
      if (
        !confirm(this.$t("message.image_generator.confirm_delete_template"))
      ) {
        return;
      }
      try {
        await deletePromptTemplate(template.id);
        this.templates = this.templates.filter(t => t.id !== template.id);
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.templates_error"));
      }
    }
  }
};
</script>
